import { del, get, patch, post, Request, Response } from '@/api'
import { QuoteSort } from '@/modules/quote/types'
import { PageableResponse } from '@/types'
import {
  CollectionPatchDto,
  CommentDto,
  FilterIdentifier,
  NewQuotesCount,
  Quote,
  QuoteActivity,
  QuoteId,
  QuoteItemUpdate,
  QuoteRequest,
  QuoteRequestForProduct,
  QuoteRequestItemDto,
  QuoteRequestPatch,
  QuoteRequestPostDto,
  QuoteUpdate,
  RejectionDto,
  ShortQuote,
  ShortQuoteRequestItem
} from '@/types/index'

export default {
  getQuotes: (
    page: number,
    size: number,
    sort?: QuoteSort,
    filter?: string,
    keyword?: string
  ): Promise<Response<PageableResponse<ShortQuote>>> => {
    const config: Request = {
      params: {
        page: page - 1,
        size,
        sort,
        filter,
        keyword
      }
    }
    return get<PageableResponse<ShortQuote>>('/quotes', config)
  },
  getQuote: (id: string, isGuest = false): Promise<Response<Quote>> => {
    return get<Quote>((isGuest ? '/guest' : '') + '/quotes/' + id)
  },
  getQuoteUpdateStreamPath: (id: string, isGuest = false): string => {
    return (isGuest ? '/guest' : '') + '/quotes-sse/' + id
  },
  printQuote: (id: string, isGuest = false): Promise<Response<Blob>> => {
    return get<Blob>((isGuest ? '/guest' : '') + '/quotes/' + id + '/print', {
      responseType: 'blob'
    })
  },
  rejectQuote: (id: string, comment: RejectionDto, isGuest = false): Promise<Response<string>> => {
    return post<string, RejectionDto>(
      (isGuest ? '/guest' : '') + '/quotes/' + id + '/reject',
      comment
    )
  },
  archiveQuote: (id: string): Promise<Response<string>> => {
    return post<string, QuoteId>('/quotes/actions/archive', {
      id: id
    })
  },
  dearchiveQuote: (id: string): Promise<Response<string>> => {
    return post<string, QuoteId>('/quotes/actions/dearchive', {
      id: id
    })
  },
  createQuoteRequest: (quoteRequest: QuoteRequestPostDto): Promise<Response<string>> => {
    return post<string, QuoteRequestPostDto>('/quote-requests/', quoteRequest)
  },
  getQuoteRequest: (id: string): Promise<Response<QuoteRequest>> => {
    return get<QuoteRequest>('/quote-requests/' + id)
  },
  deleteQuoteRequest: (id: string): Promise<Response<string>> => {
    return del<string>('/quote-requests/' + id)
  },
  updateQuoteRequest: (
    requestId: string,
    requestPatch: QuoteRequestPatch
  ): Promise<Response<string>> => {
    return patch<string, CollectionPatchDto>('/quote-requests/' + requestId, requestPatch)
  },
  addToQuoteRequest: (
    requestId: string,
    itemPostDTO: ShortQuoteRequestItem
  ): Promise<Response<string>> => {
    return post<string, ShortQuoteRequestItem>('/quote-requests/' + requestId, itemPostDTO)
  },
  updateQuoteRequestItem: (
    requestId: string,
    itemId: string,
    update: QuoteRequestItemDto
  ): Promise<Response<string>> => {
    return patch<string, QuoteRequestItemDto>(
      `/quote-requests/${requestId}/items/${itemId}`,
      update
    )
  },
  deleteRequestItem: (requestId: string, itemId: string): Promise<Response<string>> => {
    return del<string>('/quote-requests/' + requestId + '/items/' + itemId)
  },
  sendQuoteRequest: (requestId: string): Promise<Response<string>> => {
    return post<string, unknown>('/quote-requests/' + requestId + '/send')
  },
  getDraftQuoteRequestForProduct: (
    productId: string
  ): Promise<Response<QuoteRequestForProduct[]>> => {
    return get<QuoteRequestForProduct[]>('/quote-requests/products/' + productId)
  },
  isProductInDraftQuoteRequest: (productId: number): Promise<Response<boolean>> => {
    return get<boolean>('/quote-requests/' + productId + '/hasProduct')
  },
  updateQuoteItem: (
    quoteId: string,
    itemId: string,
    update: QuoteItemUpdate,
    isGuest = false
  ): Promise<Response<string>> => {
    return patch<string, QuoteItemUpdate>(
      (isGuest ? '/guest' : '') + '/quotes/' + quoteId + '/items/' + itemId,
      update
    )
  },
  updateQuote: (
    quoteId: string,
    update: QuoteUpdate,
    isGuest = false
  ): Promise<Response<string>> => {
    return patch<string, QuoteUpdate>((isGuest ? '/guest' : '') + '/quotes/' + quoteId, update)
  },
  countNewQuotes: (): Promise<Response<NewQuotesCount>> => {
    return get<NewQuotesCount>('/quotes/new-count')
  },
  getQuoteFilters: (filter?: string, keyword?: string): Promise<Response<FilterIdentifier[]>> => {
    const config: Request = {
      params: {
        filter,
        keyword
      }
    }
    return get<FilterIdentifier[]>('/quotes/filters', config)
  },
  getActivityEntries: (quoteId: string, isGuest = false): Promise<Response<QuoteActivity[]>> => {
    return get<QuoteActivity[]>((isGuest ? '/guest' : '') + '/quotes/' + quoteId + '/activity')
  },
  getActivityEntriesUpdateStreamPath: (quoteId: string, isGuest = false): string => {
    return (isGuest ? '/guest' : '') + '/quotes/' + quoteId + '/activity-sse'
  },
  addActivityEntry: (
    quoteId: string,
    commentDto: CommentDto,
    isGuest = false
  ): Promise<Response<string>> => {
    return post<string, CommentDto>(
      (isGuest ? '/guest' : '') + '/quotes/' + quoteId + '/activity',
      commentDto
    )
  },
  orderGuestQuote: (id: string): Promise<Response<string>> => {
    return post<string, Record<string, unknown>>(
      '/guest/quote/order',
      {},
      { params: { quoteId: id } }
    )
  },
  loadDatasheet: (id: string, itemId: string, isGuest = false): Promise<Response<Blob>> => {
    return get<Blob>(
      (isGuest ? '/guest' : '') + '/quotes/' + id + '/items/' + itemId + '/datasheet',
      {
        responseType: 'blob'
      }
    )
  }
}
